'use client';

import { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Link from 'next/link';
import Popover from '../Popover/Popover';
import Icon from '../Icon';
import Flag from '../Flag/Flag';
import s from './ExportSelect.module.scss';
import { t } from '../../i18n';

const HOLDINGS_FILTERS_STORAGE_KEY = 'holdingsFilters';
const SELECTED_COUNTRY_STORAGE_KEY = 'selectedCountry';

const ExportSelect = ({
    onPdf,
    onImage,
    onXlsx,
    activeTab = null,
    openPopoverOverButton = false,
}) => {
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);
    const popoverRef = useRef(null);

    const togglePopover = () => {
        setIsPopoverOpen(!isPopoverOpen);
    };

    const handleClickOutside = (event) => {
        if (popoverRef.current && !popoverRef.current.contains(event.target)) {
            setIsPopoverOpen(false);
        }
    };

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handlePdfClick = (e) => {
        setIsPopoverOpen(false);
        if (onPdf) onPdf(e);
    };
    const handleImageClick = (e) => {
        setIsPopoverOpen(false);
        if (onImage) onImage(e);
    };
    const handleXlsxClick = (e) => {
        setIsPopoverOpen(false);
        if (onXlsx) onXlsx(e);
    };

    return (
        <Popover
            // minWidth="300px"
            onClick={togglePopover}
            isPopoverOpen={isPopoverOpen}
            popoverRef={popoverRef}
            openPopoverOverButton={openPopoverOverButton}
            modifiers={['Tight', 'RightAligned']}
            content={
                <ul className={s.ExportList}>
                    {onXlsx && (
                        <li className={s.ExportListItem}>
                            <button
                                className={s.Button}
                                onClick={handleXlsxClick}>
                                <span className={s.ExportType}>XLSX</span>
                                <div className={s.DownloadIconContainer}>
                                    <Icon
                                        name="Download"
                                        className={s.DownloadIcon}
                                    />
                                </div>
                            </button>
                        </li>
                    )}
                    {onPdf && (
                        <li className={s.ExportListItem}>
                            <button
                                className={s.Button}
                                onClick={handlePdfClick}>
                                <span className={s.ExportType}>PDF</span>
                                <div className={s.DownloadIconContainer}>
                                    <Icon
                                        name="Download"
                                        className={s.DownloadIcon}
                                    />
                                </div>
                            </button>
                        </li>
                    )}
                    {onImage && (
                        <li className={s.ExportListItem}>
                            <button
                                className={s.Button}
                                onClick={handleImageClick}>
                                <span className={s.ExportType}>Image</span>
                                <div className={s.DownloadIconContainer}>
                                    <Icon
                                        name="Download"
                                        className={s.DownloadIcon}
                                    />
                                </div>
                            </button>
                        </li>
                    )}
                </ul>
            }>
            <div
                className={classNames(s.ExportSelectorButton, {
                    [s.Open]: isPopoverOpen,
                })}>
                <div className={s.ExportSelectorIconWrapper}>
                    <span className={s.ExportSelectorText}>Export as</span>
                </div>
                <Icon
                    name="ArrowRightBlack"
                    className={classNames(s.ExportSelectorArrowIcon, {
                        [s.PopoverOverButton]: openPopoverOverButton,
                    })}
                />
            </div>
        </Popover>
    );
};

ExportSelect.propTypes = {
    countries: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string.isRequired,
            code: PropTypes.string.isRequired,
            url: PropTypes.string,
            topCompanies: PropTypes.array,
            data: PropTypes.any,
        })
    ).isRequired,
    onExportSelect: PropTypes.func,
    activeTab: PropTypes.string,
};

const CountryList = ({ countries, onExportSelect, activeTab }) => {
    const handleClick = (e, country) => {
        onExportSelect(country);
    };

    return (
        <ul className={s.CountryList}>
            {countries
                .slice()
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((country) => {
                    const countryUrl =
                        activeTab !== 'company' && !!activeTab
                            ? `${country.url}${activeTab}`
                            : country.url;

                    return (
                        <li key={country.code} className={s.CountryItem}>
                            <Link
                                href={countryUrl}
                                onClick={(e) => handleClick(e, country)}
                                className={s.CountryLink}>
                                <Flag countryCode={country.code} />
                                <span className={s.CountryName}>
                                    {country.name}
                                </span>
                            </Link>
                        </li>
                    );
                })}
        </ul>
    );
};

export default ExportSelect;
